import type { FrontendErrorGroup, FrontendErrorsCategory, FrontendErrorsClientLoss } from "@/features/observability/types/frontend-errors.types";

const MONTHS: string[] = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const NUMBER_FORMAT = new Intl.NumberFormat("es-ES");

const pad = (value: number): string => String(value).padStart(2, "0");

const count = (value: number | null): string => (value === null ? "—" : NUMBER_FORMAT.format(value));

const day = (ms: number): string => {
    const date = new Date(ms);
    return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
};

const hourMinute = (ms: number): string => {
    const date = new Date(ms);
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const dateTime = (ms: number): string => `${day(ms)} ${hourMinute(ms)}`;

const dateTimeSeconds = (ms: number): string => `${dateTime(ms)}:${pad(new Date(ms).getSeconds())}`;

const nullableDateTime = (ms: number | null): string => (ms === null ? "—" : dateTime(ms));

// Relative to the reader's asOf, never to the viewer's clock: the same answer always reads the same.
const sinceAsOf = (ms: number, asOf: number): string => {
    const minutes = Math.floor(Math.max(0, asOf - ms) / MINUTE_MS);
    if (minutes < 1) return "hace menos de 1 min";
    if (minutes < 60) return `hace ${minutes} min`;
    return dateTime(ms);
};

const freshness = (lastReceivedAt: number | null, asOf: number): string =>
    lastReceivedAt === null ? "Sin eventos en el periodo" : `Último evento recibido ${sinceAsOf(lastReceivedAt, asOf)}`;

const bucketUnit = (bucketMs: number): string => {
    if (bucketMs === DAY_MS) return "día";
    if (bucketMs === HOUR_MS) return "hora";
    if (bucketMs % DAY_MS === 0) return `${bucketMs / DAY_MS} días`;
    if (bucketMs % HOUR_MS === 0) return `${bucketMs / HOUR_MS} horas`;
    if (bucketMs % MINUTE_MS === 0) return `${bucketMs / MINUTE_MS} min`;
    return `${Math.round(bucketMs / 1000)} s`;
};

const short = (value: string | null): string => (value === null ? "—" : value.slice(0, 8));

type CategoryStyle = { label: string; color: string };

// Same texts and colours as the command-center cards (CommandCenterErrors.css, keyed by the same enum). Hex mirrors of
// the design-system tokens: recharts paints SVG attributes, where var() is not reliable.
const CATEGORY_STYLES: Record<FrontendErrorsCategory, CategoryStyle> = {
    technical_fault: { label: "Fallo técnico", color: "#D80027" },
    business_rejection: { label: "Rechazo de negocio", color: "#0052B4" },
    user_cancelled: { label: "Cancelado por el usuario", color: "#94A3B8" },
    warning: { label: "Aviso", color: "#FFDA44" },
    unknown: { label: "Sin clasificar", color: "#8A56E2" },
};

const CATEGORIES: FrontendErrorsCategory[] = ["technical_fault", "business_rejection", "user_cancelled", "warning", "unknown"];

const isCategory = (value: string | null): value is FrontendErrorsCategory =>
    value !== null && CATEGORIES.some((category) => category === value);

const categoryStyle = (value: string | null): CategoryStyle => {
    if (isCategory(value)) return CATEGORY_STYLES[value];
    return { label: value === null ? "—" : value, color: "#CCCCCC" };
};

// SC-001: a sum missing one of its three parts is not a total, so any unknown part makes it unknown.
const lossTotal = (loss: FrontendErrorsClientLoss | null): number | null => {
    if (loss === null || loss.overflow === null || loss.expired === null || loss.dropped === null) return null;
    return loss.overflow + loss.expired + loss.dropped;
};

// The reader groups by (fingerprint, category, errorCode): one fingerprint can head several rows.
const groupKey = (group: FrontendErrorGroup): string => JSON.stringify([group.fingerprint, group.category, group.errorCode]);

const GAP_REASONS = new Map<string, string>([
    ["saturated", "Buffer saturado"],
    ["memory_only", "Solo memoria"],
    ["unflushed_on_exit", "Cerró antes de enviar"],
    ["disabled", "Ingesta apagada"],
    ["server_outdated", "Servidor antiguo"],
    ["expired_unsent", "Caducó sin enviar"],
]);

const gapReason = (reason: string): string => {
    const label = GAP_REASONS.get(reason);
    return label === undefined ? reason : label;
};

export const frontendErrorsFormat = {
    count,
    day,
    hourMinute,
    dateTime,
    dateTimeSeconds,
    nullableDateTime,
    sinceAsOf,
    freshness,
    bucketUnit,
    short,
    categoryStyle,
    isCategory,
    gapReason,
    lossTotal,
    groupKey,
    CATEGORIES,
};
